import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, CardBody, Col, Container, Row,
  Form, FormGroup, Label, Input, Button, CardHeader
} from 'reactstrap';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useHistory } from "react-router-dom";
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../BaseUrl/cropUtils';
import ApiClient from '../BaseUrl/ApiClient';

import '../BaseUrl/crop.css'

function AddCategories() {
  const history = useHistory();
  const [formData, setFormData] = useState({
    name: '',
    categoryPhoto: null,
    removeLogo: false, // Add removeLogo field
  });

  const [loading, setLoading] = useState(false);
  const [categoryPhotoPreview, setCategoryPhotoPreview] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('No file chosen');
  const fileInputRef = useRef(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  useEffect(() => {
    return () => {
      if (categoryPhotoPreview) {
        URL.revokeObjectURL(categoryPhotoPreview);
      }
    };
  }, [categoryPhotoPreview]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFileName(file.name);
      const reader = new FileReader();

      reader.onload = () => {
        setImageToCrop(reader.result);
        setShowCropper(true);
        e.target.value = '';
      };

      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSaveCrop = async () => {
    try {
      const croppedImage = await getCroppedImg(
        imageToCrop,
        croppedAreaPixels,
        'category-photo.jpeg'
      );

      const categoryPhoto = new File([croppedImage], 'category-photo.jpeg', {
        type: 'image/jpeg'
      });

      setFormData(prev => ({
        ...prev,
        categoryPhoto: categoryPhoto,
        removeLogo: false // Reset removeLogo when new image is added
      }));

      const previewUrl = URL.createObjectURL(croppedImage);
      setCategoryPhotoPreview(previewUrl);
      setShowCropper(false);
    } catch (error) {
      toast.error('Failed to crop image');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      categoryPhoto: null,
      removeLogo: true
    }));
    setCategoryPhotoPreview(null);
    setSelectedFileName('No file chosen');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();

      if (formData.name && formData.name.trim() !== '') {
        formDataToSend.append('name', formData.name.trim());
      }

      if (formData.categoryPhoto) {
        formDataToSend.append('categoryPhoto', formData.categoryPhoto);
      }

      // Append removeLogo as a string 'true' or 'false'
      formDataToSend.append('removeLogo', formData.removeLogo.toString());

      const response = await ApiClient().post('admin/categories', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;

      if (data.responseCode === 200 || (data.responseMessage && data.responseMessage.some(msg => msg.toLowerCase().includes('success')))) {
        toast.success('Category added successfully!');
        setFormData({
          name: '',
          categoryPhoto: null,
          removeLogo: false
        });
        setCategoryPhotoPreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setTimeout(() => {
          history.push('/all-categories');
        }, 1500);
      } else {
        throw new Error(data.responseMessage?.join(', ') || 'Failed to add category');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.responseMessage?.join(', ') || error.message;
      toast.error(`${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags>
          <title>Add Categories</title>
        </MetaTags>
        <ToastContainer />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader className='d-flex align-items-center'>
                <Link to="/all-categories">
                  <Button color="primary" className="btn">
                    <i className="mdi mdi-arrow-left"></i>
                  </Button>
                </Link>
                <h4 className="card-title mb-0 ms-2">Add Categories</h4>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Category Name</Label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Enter category name"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Category Photo</Label>

                        <div className="d-flex">
                          <Button
                            type="button"
                            color="primary"
                            onClick={() => fileInputRef.current.click()}
                            style={{
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0
                            }}
                          >
                            Choose File
                          </Button>

                          <Input
                            type="text"
                            readOnly
                            value={selectedFileName}
                            style={{
                              flex: 1, cursor: "pointer", borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0
                            }}
                            onClick={() => fileInputRef.current.click()}
                          />

                          <input
                            type="file"
                            name="file"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            accept="image/*"
                            style={{ display: "none" }}
                          />
                        </div>

                        {categoryPhotoPreview && (
                          <div className="mt-2 d-flex align-items-center">
                            <img
                              src={categoryPhotoPreview}
                              alt="Category preview"
                              style={{
                                maxWidth: '50%',
                                maxHeight: '50px',
                                borderRadius: '4px',
                                objectFit: 'contain',
                                border: '1px solid #ddd',
                                marginRight: '10px'
                              }}
                            />
                           <Button
  color="danger"
  size="sm"
  className="btn"
  style={{
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    color: "rgb(189, 9, 9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    marginBottom: "25px",

    boxShadow: "none"
  }}
  onClick={handleRemoveLogo}
>
  x
</Button>

                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="mt-3">
                    <Button type="submit" color="primary" disabled={loading}>
                      {loading ? 'Adding...' : 'Add Category'}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showCropper && (
          <div className="crop-modal">
            <div className="crop-modal-content">
              <h4>Crop your category photo</h4>

              <div className="crop-container">
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={3 / 2}
                  cropShape="rect"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="crop-controls">
                <label>
                  Zoom: {zoom.toFixed(1)}x
                </label>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                />
              </div>

              <div className="crop-buttons">
                <Button
                  color="secondary"
                  onClick={() => setShowCropper(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  onClick={handleSaveCrop}
                >
                  Save Crop
                </Button>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

export default AddCategories;